import { useEffect } from "react";
import { BrowserRouter, Route, Routes, Navigate, useNavigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useDraftStore } from "@/shared/store/draft";
import { useErrorToastStore } from "@/shared/store/errorToast";
import { apiErrorMessage } from "@/shared/api/client";
import { Shell } from "./Shell";
import { Stub } from "@/pages/Stub";
import { ProductStep } from "@/pages/CampaignWizard/steps/ProductStep";
import { BudgetStep } from "@/pages/CampaignWizard/steps/BudgetStep";
import { BloggersStep } from "@/pages/CampaignWizard/steps/BloggersStep";
import { CreativeStep } from "@/pages/CampaignWizard/steps/CreativeStep";
import { ReviewStep } from "@/pages/CampaignWizard/steps/ReviewStep";
import { PaymentStep } from "@/pages/CampaignWizard/steps/PaymentStep";
import { CampaignKanban } from "@/pages/CampaignKanban";
import { CampaignsPage } from "@/pages/Campaigns";
import { CatalogPage } from "@/pages/Catalog";
import { CollectionsPage } from "@/pages/Collections";
import { CollectionDetailPage } from "@/pages/CollectionDetail";
import { InboxPage } from "@/pages/Inbox";
import { AnalyticsPage } from "@/pages/Analytics";
import { DocumentsPage } from "@/pages/Documents";
import { CreativesPage } from "@/pages/Creatives";
import { BloggerProfile } from "@/pages/BloggerProfile";
import { HelpPage } from "@/pages/Help";
import { BillingPage } from "@/pages/Billing";
import { BloggerInboxPage } from "@/pages/BloggerCabinet/BloggerInbox";
import { BloggerBalancePage } from "@/pages/BloggerCabinet/BloggerBalance";
import { RequireAuth } from "./RequireAuth";

/** Old per-campaign chat URL → unified inbox view filtered to the campaign.
 *  Keeps the URL working for bookmarks / external links from kanban. */
function CampaignChatsRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/inbox?campaign=${id ?? ""}`} replace />;
}

/** Entry point for a fresh campaign wizard run. Resets the draft store so
 *  budget / blogger selection / variants from any earlier run don't bleed in,
 *  then sends the user to the first step. Draft-resume flows (clicking a
 *  draft card on /account/projects) skip this and route straight to the
 *  saved wizard_step, so they keep their state. */
function NewCampaignEntry() {
  const navigate = useNavigate();
  const reset = useDraftStore((s) => s.reset);
  useEffect(() => {
    reset();
    navigate("/campaign/new/product", { replace: true });
  }, [reset, navigate]);
  return null;
}

const qc = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: false },
    mutations: {
      onError: (err) => useErrorToastStore.getState().show(apiErrorMessage(err)),
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <RequireAuth>
        <Routes>
          <Route path="/" element={<Navigate to="/account/projects" replace />} />
          <Route element={<Shell />}>
            <Route path="/account/projects" element={<CampaignsPage />} />
            <Route path="/catalog"               element={<CatalogPage />} />
            <Route path="/collections"           element={<CollectionsPage />} />
            <Route path="/collections/:id"       element={<CollectionDetailPage />} />
            <Route path="/inbox"                 element={<InboxPage />} />
            <Route path="/campaign/new"          element={<NewCampaignEntry />} />
            <Route path="/campaign/new/product"  element={<ProductStep />} />
            <Route path="/campaign/new/budget"   element={<BudgetStep />} />
            <Route path="/campaign/new/bloggers" element={<BloggersStep />} />
            <Route path="/campaign/new/creative" element={<CreativeStep />} />
            <Route path="/campaign/new/review"   element={<ReviewStep />} />
            <Route path="/campaign/new/payment"  element={<PaymentStep />} />
            <Route path="/campaign/:id"          element={<CampaignKanban />} />
            <Route path="/campaign/:id/chats"    element={<CampaignChatsRedirect />} />
            <Route path="/blogger/:id"           element={<BloggerProfile />} />
            <Route path="/creatives"             element={<CreativesPage />} />
            <Route path="/analytics"             element={<AnalyticsPage />} />
            <Route path="/docs"                  element={<DocumentsPage />} />
            <Route path="/help"                  element={<HelpPage />} />
            <Route path="/billing"              element={<BillingPage />} />
            <Route path="/blogger-cabinet/inbox"   element={<BloggerInboxPage />} />
            <Route path="/blogger-cabinet/balance" element={<BloggerBalancePage />} />
          </Route>
          <Route path="*" element={<Stub title="Страница не найдена" />} />
        </Routes>
        </RequireAuth>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
